import { useMemo, useState } from 'react';
import { Download, Search as SearchIcon, ShoppingCart } from '@/components/icons';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import { listPickLists, pickListExportPath, pickListPhotos, type PickList } from '@/api/pickList';
import { downloadExcel } from '@/lib/downloadExcel';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';
import { PickListModal } from './PickListModal';
import { PickListPhotosModal } from './PickListPhotos';
import { orderRef, trackByOrderId } from '@/lib/orderRef';

const STATUS_TONE: Record<string, 'accent' | 'success' | 'muted'> = {
  'In Progress': 'accent',
  Completed: 'success',
  Cancelled: 'muted',
};

const CHANNELS: [string, string][] = [['', 'All'], ['online', 'Online'], ['regular', 'Regular']];

export function PickListPage() {
  const [status, setStatus] = useState('In Progress');
  const [photosFor, setPhotosFor] = useState<PickList | null>(null);
  const [channel, setChannel] = useState('');
  const [partial, setPartial] = useState<'' | 'partial' | 'full'>('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  // Pick date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const filter = useMemo(
    () => ({ status: status || undefined, channel: channel || undefined, partial: partial || undefined, from: range?.from, to: range?.to }),
    [status, channel, partial, range],
  );

  const query = useQuery({
    queryKey: ['pick-list', filter],
    queryFn: () => listPickLists(filter),
    refetchInterval: 60_000,
  });
  const data = query.data ?? [];

  const toast = useToast();
  // Export every matching row (server-side, no cap) — the table shows the latest 200.
  function onExport() {
    downloadExcel(pickListExportPath({ ...filter, search: search.trim() || undefined }), 'GRHAMOY_Pick_Lists.xlsx', 120_000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  return (
    <div>
      <SectionHeader
        title="Pick List"      />

      <div className="mb-3 flex gap-1">
        {CHANNELS.map(([v, label]) => (
          <button
            key={v}
            onClick={() => setChannel(v)}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors sm:px-3 sm:py-1.5 sm:text-sm ${channel === v ? 'bg-accent text-white' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
          >
            {label === 'Online' ? <><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</> : label}
          </button>
        ))}
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <SearchIcon
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search pick lists"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>In Progress</option>
            <option>Completed</option>
            <option>Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Order" className="col-span-3 sm:col-auto">
          <Select value={partial} onChange={(e) => setPartial(e.target.value as '' | 'partial' | 'full')} title="Partial dispatch = this dispatch order covers only part of the sales order">
            <option value="">All orders</option>
            <option value="full">Full orders</option>
            <option value="partial">Partial dispatch</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <button
          type="button"
          onClick={onExport}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          title="Export every pick list matching these filters to Excel"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <Table<PickList>
        storageKey="pick-list"
        paginated
        title="Pick Lists"
        count={`${data.length} records`}
        rows={data}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={trackByOrderId(channel === 'online', { key: 'orderId', header: 'Order ID', cell: (r) => <span className="font-extrabold text-accent">{orderRef(r.dispatchOrder?.enquiry)}</span> }, ['pickNo','do','enq'], [
          { key: 'pickNo', header: 'PL #', cell: (r) => <span className="font-extrabold text-accent">{r.pickNo}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.pickDate) },
          {
            key: 'do', header: 'DO Ref',
            cell: (r) => (
              <span className="inline-flex items-center gap-1.5">
                {r.dispatchOrder?.doNo ?? '—'}
                {(r.dispatchOrder?.isPartial || r.dispatchOrder?.enquiry?.balancePending) && (
                  <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 py-px text-[9px] font-black uppercase tracking-wide text-amber-600" title="Partial dispatch — this DO covers only part of the order">Partial</span>
                )}
              </span>
            ),
          },
          { key: 'enq', header: 'Order Ref', cell: (r) => orderRef(r.dispatchOrder?.enquiry) },
          { key: 'channel', header: 'Channel', align: 'center', cell: (r) => (r.dispatchOrder?.enquiry?.source === 'Shopify' ? <Badge tone="info"><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</Badge> : <span className="text-[var(--text-muted)]">Regular</span>) },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.onlineCustomer ? `${r.onlineCustomer} (Shopify)` : r.dispatchOrder?.party?.name ?? '—'}</span> },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.items.length },
          { key: 'qty', header: 'Qty', align: 'right', cell: (r) => r.items.reduce((sum, it) => sum + Number(it.doQty), 0) },
          {
            key: 'checked',
            header: 'Done',
            align: 'right',
            cell: (r) => `${r.items.filter((it) => it.isChecked).length}/${r.items.length}`,
          },
          { key: 'assignedTo', header: 'Assigned', cell: (r) => r.assignedTo },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <Badge tone={STATUS_TONE[r.status] ?? 'muted'}>{r.status}</Badge>,
          },
          {
            key: 'actions',
            header: '',
            width: '190px',
            cell: (r) => (
              <div className="flex flex-nowrap items-center gap-1.5">
                <Button variant="primary" size="sm" onClick={() => setOpenId(r.id)}>
                  {r.status === 'Completed' ? 'View' : 'Pick →'}
                </Button>
                {r.status !== 'Cancelled' && r.dispatchOrder?.enquiry?.source !== 'Shopify' && (
                  <Button variant="glass" size="sm" onClick={() => setPhotosFor(r)} title="Add or remove photos of the segregated items">
                    Photos{pickListPhotos(r).length ? ` (${pickListPhotos(r).length})` : ''}
                  </Button>
                )}
              </div>
            ),
          },
        ])}
      />

      <PickListModal open={openId !== null} pickListId={openId} onClose={() => setOpenId(null)} />
      <PickListPhotosModal pickList={photosFor} onClose={() => setPhotosFor(null)} />
    </div>
  );
}
